import {
	RuleElementType,
	RuleLifecycle,
	RuleMetadataOperator,
	RuleSeverity,
	RuleType,
	RuleValidationMethod,
} from '@atlas/types'
import { z } from 'zod'

/** A classification tuple: axis code → selected value codes (ADR-0007). */
const classificationSchema = z.record(z.string(), z.array(z.string()))

/**
 * Element types a rule governs; `[]` means "applies to all" (ADR-0018).
 *
 * This validation is load-bearing, not a convenience: `applies_to` is a `jsonb`
 * column, so the database accepts any array. An unknown code that gets past here
 * would persist and then silently never match during resolution, which reads as
 * "this rule mysteriously does not apply" rather than as an error.
 */
const appliesToSchema = z.array(z.enum(RuleElementType))

/** The machine-readable form of a `metadata` rule's check: one field path, one operator. */
const metadataCheckSchema = z.object({
	field: z.string().min(1),
	operator: z.enum(RuleMetadataOperator),
})

/**
 * `metadataCheck` belongs to the `metadata` method alone. Rejecting it elsewhere is
 * the only guard the column has, since it is `jsonb`.
 *
 * It is deliberately NOT required when the method IS `metadata`: a rule carrying the
 * method with no check is legal and reports as uncheckable, and requiring it would
 * 400 a method change submitted on its own.
 *
 * `resolvedValidationMethod` is caller-supplied rather than read straight off `data`
 * so create and update can each resolve an omitted method the way their entity does:
 * create defaults to `MULTIMODAL_LLM` (`Rule.fromRequest`), update leaves the existing
 * value untouched (`Rule.updateFromPartial`), so `undefined` there still means "unknown".
 */
function metadataCheckBelongsToMetadataMethod(data: {
	resolvedValidationMethod?: RuleValidationMethod
	metadataCheck?: unknown
}): boolean {
	if (data.metadataCheck === undefined || data.metadataCheck === null) return true
	return data.resolvedValidationMethod === undefined || data.resolvedValidationMethod === RuleValidationMethod.METADATA
}

const METADATA_CHECK_ISSUE = {
	message: 'metadataCheck is only valid when validationMethod is metadata',
	path: ['metadataCheck'],
}

// ─── Public-facing schema (create / update inputs) ────────────────────────────

export const ruleCreateSchema = z
	.object({
		statement: z.string().min(1),
		type: z.enum(RuleType),
		severity: z.enum(RuleSeverity),
		classification: classificationSchema.default({}),
		appliesTo: appliesToSchema.default([]),
		checkDefinition: z.string().optional(),
		validationMethod: z.enum(RuleValidationMethod).optional(),
		metadataCheck: metadataCheckSchema.nullable().optional(),
	})
	.refine(
		(data) =>
			metadataCheckBelongsToMetadataMethod({
				metadataCheck: data.metadataCheck,
				resolvedValidationMethod: data.validationMethod ?? RuleValidationMethod.MULTIMODAL_LLM,
			}),
		METADATA_CHECK_ISSUE,
	)

export const ruleUpdateSchema = z
	.object({
		statement: z.string().min(1).optional(),
		type: z.enum(RuleType).optional(),
		severity: z.enum(RuleSeverity).optional(),
		classification: classificationSchema.optional(),
		appliesTo: appliesToSchema.optional(),
		checkDefinition: z.string().optional(),
		validationMethod: z.enum(RuleValidationMethod).optional(),
		metadataCheck: metadataCheckSchema.nullable().optional(),
		effectiveFrom: z.iso.datetime().optional(),
		effectiveTo: z.iso.datetime().nullable().optional(),
		lifecycle: z.enum(RuleLifecycle).optional(),
	})
	.refine((data) => !(data.lifecycle === RuleLifecycle.ACTIVE && data.checkDefinition === ''), {
		message: 'An active rule requires a non-empty checkDefinition',
		path: ['checkDefinition'],
	})
	.refine(
		(data) =>
			metadataCheckBelongsToMetadataMethod({
				metadataCheck: data.metadataCheck,
				resolvedValidationMethod: data.validationMethod,
			}),
		METADATA_CHECK_ISSUE,
	)

export type RuleCreateInput = z.infer<typeof ruleCreateSchema>
export type RuleUpdateInput = z.infer<typeof ruleUpdateSchema>

/**
 * The optional body of a rule-validation request, on all three target kinds.
 *
 * `ruleIds` narrows the run to the rules named, for a caller re-checking one rule
 * rather than the whole target. Omitting the body covers every rule that resolves,
 * so an empty array is rejected rather than read as "cover nothing".
 */
export const ruleValidationRequestSchema = z.object({
	ruleIds: z.array(z.uuid()).min(1).optional(),
})

export type RuleValidationRequestInput = z.infer<typeof ruleValidationRequestSchema>
